import React, { useState, useEffect } from 'react';
import { 
  Form, 
  Input, 
  InputNumber, 
  Card, 
  Divider, 
  Button, 
  Row, 
  Col, 
  Select, 
  Switch, 
  Tabs, 
  Space, 
  Spin,
  DatePicker,
  Table,
  Typography,
  Modal
} from 'antd';
import { SaveOutlined, ArrowLeftOutlined, PlusOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { ProductPropertiesDTO } from '@/shared/api/model';
import { useTheme } from '@/components/common/ThemeProvider';
import LoanProductPreviewPanel from './LoanProductPreviewPanel';
import LoanAgreementConfigPanel from './loan-agreement/LoanAgreementConfigPanel';

const { TabPane } = Tabs;
const { Option } = Select;
const { TextArea } = Input;

interface RequiredDocument {
  role: string;
  label: string;
  optional?: boolean;
}

const parseRequiredDocuments = (value?: string): RequiredDocument[] => {
  if (!value) {
    return [];
  }

  try {
    const parsedValue = JSON.parse(value);
    if (!Array.isArray(parsedValue)) {
      return [];
    }

    return parsedValue
      .filter((document): document is RequiredDocument => !!document?.role && !!document?.label)
      .map((document) => ({
        role: document.role,
        label: document.label,
        optional: document.optional ?? false
      }));
  } catch (error) {
    console.error('Failed to parse required documents:', error);
    return [];
  }
};

interface LoanProductSettingsFormProps {
  initialData: ProductPropertiesDTO;
  onSubmit: (data: ProductPropertiesDTO) => void;
  isLoading?: boolean;
}

const LoanProductSettingsForm: React.FC<LoanProductSettingsFormProps> = ({
  initialData,
  onSubmit,
  isLoading = false
}) => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { dark } = useTheme();
  const watchedDefaultAmount = Form.useWatch('defaultAmount', form);
  const watchedDefaultTerm = Form.useWatch('defaultTerm', form);
  const watchedBaseInterestRate = Form.useWatch('baseInterestRate', form);
  const watchedRequiresDeductionForm = Form.useWatch('requiresDeductionForm', form);
  const [interestRateIsFixed, setInterestRateIsFixed] = useState(initialData?.interestRateIsFixed ?? true);
  const [requiresGuarantor, setRequiresGuarantor] = useState(initialData?.requiresGuarantor ?? false);
  const [requiresCollateral, setRequiresCollateral] = useState(initialData?.requiresCollateral ?? false);
  const [requiresDeductionForm, setRequiresDeductionForm] = useState(initialData?.requiresDeductionForm ?? false);
  const [requiresSavingsAccount, setRequiresSavingsAccount] = useState(initialData?.requiresSavingsAccount ?? false);
  const [requiresSalaryInformation, setRequiresSalaryInformation] = useState(initialData?.requiresSalaryInformation ?? false);
  const [loanAgreementConfig, setLoanAgreementConfig] = useState(initialData?.loanAgreementConfig);

  useEffect(() => {
    setLoanAgreementConfig(initialData?.loanAgreementConfig);
    form.setFieldsValue(initialData);
  }, [initialData, form]);

  // State for required documents
  const [requiredDocuments, setRequiredDocuments] = useState<RequiredDocument[]>(
    parseRequiredDocuments(initialData?.requiredDocuments)
  );
  const [documentModalVisible, setDocumentModalVisible] = useState(false);
  const [documentForm] = Form.useForm();
  const [editingDocumentIndex, setEditingDocumentIndex] = useState<number | null>(null);

  const handleSubmit = (values: ProductPropertiesDTO) => {
    try {
      // Preserve the ID from the initial data
      values.id = initialData.id;
      values.syncWithCoa = values.syncWithCoa ?? false;
      
      // Convert required documents to JSON string
      values.requiredDocuments = JSON.stringify(requiredDocuments);
      
      // Include loan agreement config
      values.loanAgreementConfig = loanAgreementConfig;
      
      // Submit the form with the updated values
      onSubmit(values);
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  const handleBack = () => {
    navigate('/admin/loan-settings/products');
  };

  // Document management functions
  const showAddDocumentModal = () => {
    documentForm.resetFields();
    documentForm.setFieldsValue({ optional: false });
    setEditingDocumentIndex(null);
    setDocumentModalVisible(true);
  };

  const showEditDocumentModal = (index: number) => {
    const document = requiredDocuments[index];
    documentForm.setFieldsValue(document);
    setEditingDocumentIndex(index);
    setDocumentModalVisible(true);
  };

  const handleDocumentModalOk = () => {
    documentForm.validateFields().then(values => {
      const newDocument: RequiredDocument = {
        role: values.role,
        label: values.label,
        optional: values.optional ?? false
      };

      if (editingDocumentIndex !== null) {
        // Update existing document
        const updatedDocuments = [...requiredDocuments];
        updatedDocuments[editingDocumentIndex] = newDocument;
        setRequiredDocuments(updatedDocuments);
      } else {
        // Add new document
        setRequiredDocuments([...requiredDocuments, newDocument]);
      }

      setDocumentModalVisible(false);
    });
  };

  const handleDeleteDocument = (index: number) => {
    const updatedDocuments = [...requiredDocuments];
    updatedDocuments.splice(index, 1);
    setRequiredDocuments(updatedDocuments);
  };

  console.log(initialData);

  return (
    <div className={dark ? 'dark-mode' : ''}>
      <Card
        title={
          <Space>
            <Button 
              icon={<ArrowLeftOutlined />} 
              onClick={handleBack}
              type="link"
            />
            Configure Loan Product: {initialData.productName}
          </Space>
        }
      >
        <Spin spinning={isLoading}>
          <Form
            form={form}
            layout="vertical"
            initialValues={initialData}
            onFinish={handleSubmit}
            scrollToFirstError
          >
            <Tabs defaultActiveKey="basic">
              <TabPane tab="Basic Information" key="basic">
                <Row gutter={24}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="glNo"
                      label="Product Code"
                      rules={[
                        { required: true, message: 'Please enter product code' },
                        { max: 100, message: 'Code cannot exceed 100 characters' }
                      ]}
                    >
                      <Input placeholder="Product Code" disabled />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="productName"
                      label="Product Name"
                      rules={[
                        { required: true, message: 'Please enter product name' },
                        { max: 255, message: 'Name cannot exceed 255 characters' }
                      ]}
                    >
                      <Input placeholder="Product Name" disabled />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={24}>
                  <Col xs={24}>
                    <Form.Item
                      name="description"
                      label="Description"
                      rules={[
                        { max: 500, message: 'Description cannot exceed 500 characters' }
                      ]}
                    >
                      <TextArea rows={4} placeholder="Product Description" />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={24}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="deductionCode"
                      label="Deduction Code"
                      rules={[
                        { max: 20, message: 'Deduction code cannot exceed 20 characters' }
                      ]}
                      tooltip="Unique deduction code for salary deductions via ESS"
                    >
                      <Input placeholder="Deduction Code" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="isActive"
                      label="Status"
                      valuePropName="checked"
                    >
                      <Switch 
                        checkedChildren="Active" 
                        unCheckedChildren="Inactive" 
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={24}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="syncWithCoa"
                      label="Sync Changes With COA"
                      valuePropName="checked"
                      tooltip="When enabled, product and fee changes will also update the linked COA. Leave off to keep product settings independent from COA changes."
                    >
                      <Switch
                        checkedChildren="Sync On"
                        unCheckedChildren="Sync Off"
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </TabPane>
              
              <TabPane tab="Interest & Amount" key="interest">
                <Divider orientation="left">Interest Rate Configuration</Divider>
                <Row gutter={24}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="baseInterestRate"
                      label="Base Interest Rate (%)"
                      rules={[
                        { required: true, message: 'Please enter base interest rate' }
                      ]}
                    >
                      <InputNumber 
                        min={0} 
                        max={100} 
                        step={0.01} 
                        style={{ width: '100%' }} 
                        placeholder="Base Interest Rate"
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="interestRateIsFixed"
                      label="Interest Rate Type"
                      valuePropName="checked"
                    >
                      <Switch 
                        checkedChildren="Fixed" 
                        unCheckedChildren="Variable" 
                        onChange={setInterestRateIsFixed}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                
                {!interestRateIsFixed && (
                  <Row gutter={24}>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        name="minimumInterestRate"
                        label="Minimum Interest Rate (%)"
                        rules={[
                          { required: !interestRateIsFixed, message: 'Please enter minimum interest rate' }
                        ]}
                      >
                        <InputNumber 
                          min={0} 
                          max={100} 
                          step={0.01} 
                          style={{ width: '100%' }} 
                          placeholder="Minimum Interest Rate"
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        name="maximumInterestRate"
                        label="Maximum Interest Rate (%)"
                        rules={[
                          { required: !interestRateIsFixed, message: 'Please enter maximum interest rate' }
                        ]}
                      >
                        <InputNumber 
                          min={0} 
                          max={100} 
                          step={0.01} 
                          style={{ width: '100%' }} 
                          placeholder="Maximum Interest Rate"
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                )}
                
                <Divider orientation="left">Loan Amount Configuration</Divider>
                <Row gutter={24}>
                  <Col xs={24} sm={8}>
                    <Form.Item
                      name="minimumAmount"
                      label="Minimum Amount"
                      rules={[
                        { required: true, message: 'Please enter minimum amount' }
                      ]}
                    >
                      <InputNumber 
                        min={0} 
                        step={100} 
                        style={{ width: '100%' }} 
                        placeholder="Minimum Amount"
                        formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                          // @ts-ignore
                        parser={value => value!.replace(/\$\s?|(,*)/g, '')}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item
                      name="maximumAmount"
                      label="Maximum Amount"
                      rules={[
                        { required: true, message: 'Please enter maximum amount' }
                      ]}
                    >
                      <InputNumber 
                        min={0} 
                        step={1000} 
                        style={{ width: '100%' }} 
                        placeholder="Maximum Amount"
                        formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                          // @ts-ignore
                        parser={value => value!.replace(/\$\s?|(,*)/g, '')}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item
                      name="defaultAmount"
                      label="Default Amount"
                    >
                      <InputNumber 
                        min={0} 
                        step={1000} 
                        style={{ width: '100%' }} 
                        placeholder="Default Amount"
                        formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                          // @ts-ignore
                        parser={value => value!.replace(/\$\s?|(,*)/g, '')}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                
                <Divider orientation="left">Term Configuration</Divider>
                <Row gutter={24}>
                  <Col xs={24} sm={8}>
                    <Form.Item
                      name="minimumTerm"
                      label="Minimum Term (months)"
                      rules={[
                        { required: true, message: 'Please enter minimum term' }
                      ]}
                    >
                      <InputNumber 
                        min={1} 
                        style={{ width: '100%' }} 
                        placeholder="Minimum Term"
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item
                      name="maximumTerm"
                      label="Maximum Term (months)"
                      rules={[
                        { required: true, message: 'Please enter maximum term' }
                      ]}
                    >
                      <InputNumber 
                        min={1} 
                        style={{ width: '100%' }} 
                        placeholder="Maximum Term"
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item
                      name="defaultTerm"
                      label="Default Term (months)"
                    >
                      <InputNumber 
                        min={1} 
                        style={{ width: '100%' }} 
                        placeholder="Default Term"
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </TabPane>
              
              <TabPane tab="Repayment" key="repayment">
                <Divider orientation="left">Repayment Configuration</Divider>
                <Row gutter={24}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="repaymentScheduleType"
                      label="Repayment Schedule Type"
                      rules={[
                        { required: true, message: 'Please select repayment schedule type' }
                      ]}
                    >
                      <Select placeholder="Select repayment schedule type">
                        <Option value="FR">Flat Rate</Option>
                        <Option value="DB">Declining Balance-Discounted</Option>
                        <Option value="DA">Declining Balance-Amortization</Option>
                        <Option value="DI">Declining Balance-Equal Installment</Option>
                        <Option value="DE">Declining Balance-Equal Due</Option>
                        <Option value="C1">Custom 1</Option>
                        <Option value="FL">Flat Loan Percentage</Option>
                        <Option value="BP">Balloon Payment</Option>
                        <Option value="FI">Flat Equal Installment</Option>
                        <Option value="DF">Declining Flat Installment</Option>

                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item
                        name="repaymentFrequency"
                        label="Repayment Frequency"
                        rules={[
                          {required: true, message: 'Please select repayment frequency'}
                        ]}
                    >
                      <Select placeholder="Select repayment frequency">
                        <Option value="DAILY">Daily</Option>
                        <Option value="WEEKLY">Weekly</Option>
                        <Option value="BI_WEEKLY">Bi-Weekly</Option>
                        <Option value="MONTHLY">Monthly</Option>
                        <Option value="QUARTERLY">Quarterly</Option>
                        <Option value="SEMI_ANNUALLY">Semi-Annually</Option>
                        <Option value="ANNUALLY">Annually</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={24}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="gracePeriodDays"
                      label="Grace Period (days)"
                    >
                      <InputNumber 
                        min={0} 
                        style={{ width: '100%' }} 
                        placeholder="Grace Period Days"
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="earlyPaymentOption"
                      label="Early Payment Option"
                      rules={[
                        { required: true, message: 'Please select early payment option' }
                      ]}
                    >
                      <Select placeholder="Select early payment option">
                        <Option value="REDUCE_INSTALLMENT_AMOUNT">Reduce Installment Amount</Option>
                        <Option value="REDUCE_LOAN_TERM">Reduce Loan Term</Option>
                        <Option value="APPLY_TO_FINAL_PAYMENT">Apply to Final Payment</Option>
                        <Option value="APPLY_TO_NEXT_INSTALLMENT">Apply to Next Installment</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={24}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="earlyPaymentFeePercentage"
                      label="Early Payment Fee (%)"
                    >
                      <InputNumber 
                        min={0} 
                        max={100} 
                        step={0.01} 
                        style={{ width: '100%' }} 
                        placeholder="Early Payment Fee"
                      />
                    </Form.Item>
                  </Col>
                </Row>
                
                <Divider orientation="left">Penalty Configuration</Divider>
                <Row gutter={24}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="penaltyTrigger"
                      label="Penalty Trigger"
                      rules={[
                        { required: true, message: 'Please select penalty trigger' }
                      ]}
                    >
                      <Select placeholder="Select penalty trigger">
                        <Option value="NONE">None</Option>
                        <Option value="AFTER_GRACE_PERIOD">After Grace Period</Option>
                        <Option value="IMMEDIATELY_AFTER_DUE_DATE">Immediately After Due Date</Option>
                        <Option value="CUSTOM">Custom</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="penaltyGracePeriodDays"
                      label="Penalty Grace Period (days)"
                    >
                      <InputNumber 
                        min={0} 
                        style={{ width: '100%' }} 
                        placeholder="Penalty Grace Period Days"
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={24}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="penaltyAmount"
                      label="Penalty Amount (fixed)"
                    >
                      <InputNumber 
                        min={0} 
                        step={100} 
                        style={{ width: '100%' }} 
                        placeholder="Penalty Amount"
                        formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                          // @ts-ignore
                        parser={value => value!.replace(/\$\s?|(,*)/g, '')}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="penaltyPercentage"
                      label="Penalty Percentage (%)"
                    >
                      <InputNumber 
                        min={0} 
                        max={100} 
                        step={0.01} 
                        style={{ width: '100%' }} 
                        placeholder="Penalty Percentage"
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </TabPane>
              
              <TabPane tab="Approval & Accounts" key="approval">
                <Divider orientation="left">Approval Configuration</Divider>
                <Row gutter={24}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="requiresGuarantor"
                      label="Requires Guarantor"
                      valuePropName="checked"
                    >
                      <Switch 
                        checkedChildren="Yes" 
                        unCheckedChildren="No" 
                        onChange={setRequiresGuarantor}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                
                {requiresGuarantor && (
                  <Row gutter={24}>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        name="minimumGuarantors"
                        label="Minimum Guarantors"
                        rules={[
                          { required: requiresGuarantor, message: 'Please enter minimum guarantors' }
                        ]}
                      >
                        <InputNumber 
                          min={1} 
                          style={{ width: '100%' }} 
                          placeholder="Minimum Guarantors"
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        name="maximumGuarantors"
                        label="Maximum Guarantors"
                        rules={[
                          { required: requiresGuarantor, message: 'Please enter maximum guarantors' }
                        ]}
                      >
                        <InputNumber 
                          min={1} 
                          style={{ width: '100%' }} 
                          placeholder="Maximum Guarantors"
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                )}
                
                <Row gutter={24}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="requiresDeductionForm"
                      label="Requires Deduction Form"
                      valuePropName="checked"
                      tooltip="If enabled, borrowers must fill a deduction form when applying for this loan product"
                    >
                      <Switch 
                        checkedChildren="Yes" 
                        unCheckedChildren="No" 
                        onChange={setRequiresDeductionForm}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                
                <Row gutter={24}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="requiresCollateral"
                      label="Requires Collateral"
                      valuePropName="checked"
                    >
                      <Switch 
                        checkedChildren="Yes" 
                        unCheckedChildren="No" 
                        onChange={setRequiresCollateral}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                
                <Row gutter={24}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="requiresSalaryInformation"
                      label="Requires Salary Information"
                      valuePropName="checked"
                    >
                      <Switch 
                        checkedChildren="Yes" 
                        unCheckedChildren="No" 
                        onChange={setRequiresSalaryInformation}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                
                <Row gutter={24}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="requiredDocuments"
                      label="Required Documents"
                    >
                      <Table
                        dataSource={requiredDocuments}
                        columns={[
                          {
                            title: 'Role',
                            dataIndex: 'role',
                            key: 'role',
                          },
                          {
                            title: 'Label',
                            dataIndex: 'label',
                            key: 'label',
                          },
                          {
                            title: 'Requirement',
                            key: 'requirement',
                            render: (_, record) => (record.optional ? 'Optional' : 'Mandatory'),
                          },
                          {
                            title: 'Actions',
                            key: 'actions',
                            render: (_, record, index) => (
                              <Space>
                                <Button 
                                  icon={<EditOutlined />} 
                                  onClick={() => showEditDocumentModal(index)}
                                />
                                <Button 
                                  icon={<DeleteOutlined />} 
                                  onClick={() => handleDeleteDocument(index)}
                                />
                              </Space>
                            ),
                          },
                        ]}
                      />
                      <Button 
                        icon={<PlusOutlined />} 
                        onClick={showAddDocumentModal}
                      >
                        Add Document
                      </Button>
                    </Form.Item>
                  </Col>
                </Row>
              </TabPane>
              
              <TabPane tab="Advanced Settings" key="advanced">
                <Divider orientation="left">Loan Closure Settings</Divider>
                <Row gutter={24}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="closeLoanFormula"
                      label="Close Loan Formula"
                    >
                      <Select placeholder="Select close loan formula">
                        <Option value={1}>Principal balance + past Interest Due + Current Interest Due</Option>
                        <Option value={2}>Principal balance + past Interest Due + Current Interest Due + Half Future Interest Due</Option>
                        <Option value={3}>Principal balance + past Interest Due + Current Interest Due + Full Future Interest Due</Option>
                        <Option value={4}>Next 1 installment</Option>
                        <Option value={5}>Next 2 installments</Option>
                        <Option value={6}>Next 3 installments</Option>
                        <Option value={7}>Next 4 installments</Option>
                        <Option value={8}>Next 5 installments</Option>
                        <Option value={9}>Next 6 installments</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="publicHolidayHandling"
                      label="Public Holiday And Non Working Day"
                    >
                      <Select placeholder="Select holiday handling">
                        <Option value={1}>Don't Change Due Dates</Option>
                        <Option value={2}>Change Due Date To Previous Working Day</Option>
                        <Option value={3}>Change Due Date To Next Working Day</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
                
                <Divider orientation="left">Payment Settings</Divider>
                <Row gutter={24}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="installmentRoundingPrecision"
                      label="Installment Rounding (Decimal Places)"
                      tooltip="Controls how installment amounts are rounded. 0 = whole number (e.g. 228,646), 2 = 2 decimal places (e.g. 228,645.83). Leave blank to use system default (2)."
                    >
                      <Select placeholder="Select rounding precision" allowClear>
                        <Option value={0}>0 - Whole Number (e.g. 228,646)</Option>
                        <Option value={1}>1 Decimal Place (e.g. 228,645.8)</Option>
                        <Option value={2}>2 Decimal Places (e.g. 228,645.83)</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={24}>
                  <Col xs={24} sm={8}>
                    <Form.Item
                      name="repaymentsOnLastDay"
                      label="Repayments on Last Day"
                      valuePropName="checked"
                    >
                      <Switch />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item
                      name="printReceipt"
                      label="Print Receipt"
                      valuePropName="checked"
                    >
                      <Switch />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item
                      name="interestUpfrontForFirstHalf"
                      label="Interest Upfront for First Half (Flat Rate)"
                      valuePropName="checked"
                    >
                      <Switch />
                    </Form.Item>
                  </Col>
                </Row>
                
                <Row gutter={24}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="interestPaymentYearDays"
                      label="Interest Payment Year (Days)"
                    >
                      <InputNumber 
                        min={360} 
                        max={366} 
                        style={{ width: '100%' }} 
                        placeholder="Days in Year for Interest Calculation"
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </TabPane>
              
              <TabPane tab="Advanced Options" key="advancedOptions">
                <Divider orientation="left">Loan Management Options</Divider>
                <Row gutter={24}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="activeLoanBehavior"
                      label="Active Loan Behaviour"
                      rules={[
                        { required: true, message: 'Please select active loan behaviour' }
                      ]}
                      tooltip="Defines what actions are allowed when a customer has an active loan"
                    >
                      <Select placeholder="Select active loan behaviour">
                        <Option value="ALLOW_NEW_LOAN">Allow New Loan</Option>
                        <Option value="ALLOW_REFINANCE">Allow Refinance</Option>
                        <Option value="ALLOW_TOPUP">Allow Top-up</Option>
                        <Option value="DO_NOT_ALLOW">Do Not Allow</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
                
                <Row gutter={24}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="topUpFeeCalculationBase"
                      label="Top-Up Fee Calculation Base"
                      tooltip="For top-up loans: whether fees are calculated on the total new loan amount (settlement + additional) or only on the additional amount requested by the employee."
                    >
                      <Select placeholder="Select fee calculation base">
                        <Option value="TOTAL_LOAN_AMOUNT">Total Loan Amount (Settlement + Additional)</Option>
                        <Option value="ADDITIONAL_AMOUNT_ONLY">Additional Amount Only</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>

                <Divider orientation="left">Savings Dependency Configuration</Divider>
                <Row gutter={24}>
                  <Col xs={24} sm={8}>
                    <Form.Item
                      name="requiresSavingsAccount"
                      label="Require Savings Account"
                      valuePropName="checked"
                      tooltip="If enabled, borrowers must have a savings account with sufficient balance to qualify for a loan"
                    >
                      <Switch 
                        checkedChildren="Yes" 
                        unCheckedChildren="No" 
                        onChange={(checked) => setRequiresSavingsAccount(checked)}
                      />
                    </Form.Item>
                  </Col>
                  {requiresSavingsAccount && (
                    <>
                      <Col xs={24} sm={8}>
                        <Form.Item
                          name="savingsProductGlNo"
                          label="Savings Product Code"
                          rules={[
                            { required: requiresSavingsAccount, message: 'Please enter savings product code' }
                          ]}
                          tooltip="The GL number/code of the savings product that this loan depends on"
                        >
                          <Input placeholder="Savings Product GL Number" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={8}>
                        <Form.Item
                          name="savingsBalanceMultiplier"
                          label="Savings Balance Multiplier"
                          rules={[
                            { required: requiresSavingsAccount, message: 'Please enter savings balance multiplier' }
                          ]}
                          tooltip="Maximum loan amount will be this multiple of savings balance"
                        >
                          <InputNumber 
                            min={0.1} 
                            step={0.1} 
                            style={{ width: '100%' }} 
                            placeholder="e.g., 3.0 = 3x savings balance"
                            formatter={value => `${value}x`}
                              // @ts-ignore
                            parser={value => value!.replace('x', '')}
                          />
                        </Form.Item>
                      </Col>
                    </>
                  )}
                </Row>
                
                <Divider orientation="left">Other Configuration</Divider>
                <Row gutter={24}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="expireDate"
                      label="Product Expiry Date"
                    >
                      <DatePicker 
                        style={{ width: '100%' }} 
                        placeholder="Select Expiry Date"
                        showTime={{ format: 'HH:mm' }}
                        format="YYYY-MM-DD HH:mm"
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="includeAccruedInterest"
                      label="Include Accrued Interest"
                      valuePropName="checked"
                    >
                      <Switch 
                        checkedChildren="Yes" 
                        unCheckedChildren="No" 
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </TabPane>
              
              <TabPane tab="Loan Agreement" key="loanAgreement">
                <LoanAgreementConfigPanel
                  initialConfig={initialData?.loanAgreementConfig}
                  onChange={setLoanAgreementConfig}
                />
              </TabPane>
            </Tabs>
            
            <Divider />

            {initialData?.glNo ? (
              <Card title="Loan Form Preview" style={{ marginBottom: 24 }}>
                <LoanProductPreviewPanel
                  glNo={initialData.glNo}
                  productName={initialData.productName || initialData.glNo}
                  baseInterestRate={Number(watchedBaseInterestRate ?? initialData.baseInterestRate)}
                  defaultAmount={Number(watchedDefaultAmount ?? initialData.defaultAmount) || 1000000}
                  defaultTerm={watchedDefaultTerm ?? initialData.defaultTerm ?? 12}
                  requiresDeductionForm={watchedRequiresDeductionForm ?? initialData.requiresDeductionForm ?? false}
                />
              </Card>
            ) : null}
            
            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={isLoading}>
                  Save Configuration
                </Button>
                <Button onClick={handleBack}>
                  Cancel
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Spin>
      </Card>
      
      <Modal
        title="Add/Edit Document"
        open={documentModalVisible}
        onOk={handleDocumentModalOk}
        onCancel={() => setDocumentModalVisible(false)}
      >
        <Form
          form={documentForm}
          layout="vertical"
        >
          <Form.Item
            name="role"
            label="Role"
            rules={[
              { required: true, message: 'Please enter role' }
            ]}
          >
            <Input placeholder="Role" />
          </Form.Item>
          <Form.Item
            name="label"
            label="Label"
            rules={[
              { required: true, message: 'Please enter label' }
            ]}
          >
            <Input placeholder="Label" />
          </Form.Item>
          <Form.Item
            name="optional"
            label="Requirement"
            rules={[
              { required: true, message: 'Please select document requirement' }
            ]}
          >
            <Select placeholder="Select document requirement">
              <Option value={false}>Mandatory</Option>
              <Option value={true}>Optional</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default LoanProductSettingsForm;